import {
  useCompanionControllerCreate,
  useCompanionControllerDelete,
  useCompanionControllerFindMany,
  useCompanionControllerReissuePairingToken,
  type CompanionRole,
  type CreateCompanionSessionDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { useCompanionTokens } from '../../stores/companionTokenStore';
import { useLoading } from '../../stores/loadingStore';
import { useResponseData } from '../useResponseData';

export const useCompanionSessions = () => {
  const { watchLoading } = useLoading();
  const { getToken, setToken, invalidateToken } = useCompanionTokens();
  const {
    data: companionData,
    isPending: isFetchingCompanionSessions,
    refetch: refetchCompanionSessions,
  } = useCompanionControllerFindMany();
  const { mutateAsync: createCompanionSession, isPending: isCreatingCompanionSession } =
    useCompanionControllerCreate({
      mutation: { onSuccess: () => refetchCompanionSessions() },
    });
  const { mutateAsync: deleteCompanionSession, isPending: isDeletingCompanionSession } =
    useCompanionControllerDelete({
      mutation: { onSuccess: () => refetchCompanionSessions() },
    });
  const { mutateAsync: reissuePairingToken, isPending: isReissuingPairingToken } =
    useCompanionControllerReissuePairingToken();

  const sessions = useResponseData(companionData);

  const regeneratePairingToken = async (sessionId: string) => {
    const response = await reissuePairingToken({ companionId: sessionId });
    if (response.status !== 201) return false;

    setToken(sessionId, {
      pairingToken: response.data.pairingToken,
      pairingTokenExpiresAt: response.data.pairingTokenExpiresAt,
    });

    return true;
  };

  const createSession = async (template: CompanionRole, roles: CompanionRole[]) => {
    const { data, status } = await createCompanionSession({
      data: {
        roles,
      } as CreateCompanionSessionDto,
    });

    if (status !== 201) return null;

    setToken(data.id, {
      pairingToken: data.pairingToken,
      pairingTokenExpiresAt: data.pairingTokenExpiresAt,
    });

    return data;
  };

  const removeSession = async (companionId: string) => {
    const { status } = await deleteCompanionSession({ companionId });

    if (status !== 200) return false;

    invalidateToken(companionId);
    return true;
  };

  watchLoading(
    isFetchingCompanionSessions,
    isCreatingCompanionSession,
    isDeletingCompanionSession,
    isReissuingPairingToken,
  );

  return {
    sessions,
    isCreatingCompanionSession,
    isDeletingCompanionSession,
    createSession,
    removeSession,
    regeneratePairingToken,
    refetchCompanionSessions,
    getPairingToken: getToken,
  };
};
